/**
 * Ruteo (torre ↔ TMS, JWT del TMS). Lo implementa la etapa 3 (D-34, D-35).
 *
 * El TMS es dueño del ruteo: cada troncal sincronizada desde Mara tiene 0..N rutas que arma el despacho (puntos de paso →
 * traza calculada por el motor de ruteo, más paradas con sus reglas). Una troncal sin rutas queda pendiente para despacho.
 *
 *   GET    /troncales?sinRutas=&q=&archivadas=&pagina=&tamanio=   (DESPACHO, SUPERVISION, ADMIN) → TroncalesRespuesta
 *   GET    /troncales/:id                                          (DESPACHO, SUPERVISION, ADMIN) → TroncalDetalle
 *   POST   /troncales/:id/rutas        body RutaBody               (DESPACHO, ADMIN)              → RutaGuardada
 *   PUT    /rutas/:id                  body RutaBody               (DESPACHO, ADMIN)              → RutaGuardada
 *   DELETE /rutas/:id                  baja lógica (activa=false)  (DESPACHO, ADMIN)              → Ruta
 *   POST   /rutas/calcular             body CalcularRutaBody       (DESPACHO, ADMIN)              → CalculoRuta
 *   PUT    /viajes/:id/ruta            body CambiarRutaViajeBody   (DESPACHO, ADMIN)              → ViajeDetalle
 *   GET    /ruteo/nombres?rutas=1,2&paradas=3,4                    (DESPACHO, SUPERVISION, ADMIN) → NombresRuteoRespuesta
 *
 * Editar una ruta no cambia los viajes que ya la tienen asignada: sus paradas se copian al viaje (parada_viaje).
 */
import type { Coordenadas, FechaIso, LineStringGeoJson, Pagina } from './comunes';
import type { TipoParada } from './viaje';

/** Punto de paso de una ruta, en orden. El motor calcula la traza que pasa por todos. */
export interface PuntoPaso {
  latitud: number;
  longitud: number;
  nombre?: string | null;
}

/** Parada planificada de una ruta. */
export interface ParadaRuta {
  id: number;
  /** 1..N en el orden de visita. */
  orden: number;
  nombre: string;
  tipo: TipoParada;
  ubicacion: Coordenadas;
  /** Radio del geofence en metros. */
  radioM: number;
  /** Una parada obligatoria que no se cumple abre PARADA_OMITIDA al finalizar. */
  obligatoria: boolean;
  /** Permanencia esperada en minutos (la mínima es informativa). */
  duracionMinEsperadaMin: number | null;
  /** Por encima de esta permanencia (minutos) la parada queda EXCEDIDA y se evalúa PARADA_EXCEDIDA. */
  duracionMaxEsperadaMin: number | null;
}

export interface Ruta {
  id: number;
  troncalId: number;
  nombre: string;
  puntosPaso: PuntoPaso[];
  /** Traza calculada por el motor de ruteo a partir de `puntosPaso`. */
  traza: LineStringGeoJson;
  km: number;
  duracionMin: number;
  /** Ruta que se asigna a los viajes nuevos de la troncal. Una sola activa por troncal. */
  porDefecto: boolean;
  /** false = dada de baja (DELETE): no se asigna ni se ofrece, pero los viajes que la tienen la conservan. */
  activa: boolean;
  paradas: ParadaRuta[];
  actualizadoEn: FechaIso;
}

/** Troncal sincronizada desde Mara, para el listado. */
export interface TroncalResumen {
  id: number;
  idMara: string;
  nombre: string;
  /** Código del cliente para la troncal. */
  identificacion: string;
  /** Texto libre de Mara. */
  origen: string | null;
  /** Texto libre de Mara. */
  destino: string | null;
  kmReferencia: number | null;
  archivado: boolean;
  /** Rutas activas. 0 = pendiente para despacho. */
  cantidadRutas: number;
  rutaPorDefectoId: number | null;
}

/** GET /troncales/:id. `rutas` incluye las dadas de baja (activa=false) al final. */
export interface TroncalDetalle extends TroncalResumen {
  kmCombustible: number | null;
  clienteDefecto: { idMara: string; razonSocial: string; ubicacion: Coordenadas | null } | null;
  sincronizadoEn: FechaIso;
  rutas: Ruta[];
}

/** GET /troncales: filtros por query string. */
export interface FiltroTroncales {
  /** true: solo las que no tienen ninguna ruta activa. */
  sinRutas?: boolean;
  /** Busca en nombre, identificación, origen y destino. */
  q?: string;
  /** true: incluye las archivadas (por defecto se ocultan). */
  archivadas?: boolean;
  pagina?: number;
  tamanio?: number;
}

/** Respuesta de GET /troncales, ordenada por nombre. */
export type TroncalesRespuesta = Pagina<TroncalResumen>;

/** Parada en el alta o edición de una ruta: el orden es el del arreglo. */
export interface ParadaRutaBody {
  /**
   * Id de una parada existente de esta ruta (solo en PUT). Conserva la parada, y con ella sus reglas de alerta
   * de ámbito PARADA_RUTA, aunque cambie de orden. Sin id, es una parada nueva; las existentes que no vengan se borran.
   */
  id?: number | null;
  nombre: string;
  tipo: TipoParada;
  ubicacion: Coordenadas;
  /** Si falta, GEOFENCE_RADIO_M. */
  radioM?: number | null;
  /** Default true. */
  obligatoria?: boolean;
  duracionMinEsperadaMin?: number | null;
  duracionMaxEsperadaMin?: number | null;
}

/**
 * POST /troncales/:id/rutas y PUT /rutas/:id. La traza, los km y la duración los calcula el TMS con el motor de ruteo.
 * `porDefecto: true` quita la marca a la ruta por defecto anterior de la troncal.
 */
export interface RutaBody {
  nombre: string;
  /** Al menos 2. */
  puntosPaso: PuntoPaso[];
  paradas: ParadaRutaBody[];
  porDefecto: boolean;
  activa: boolean;
}

/** POST /rutas/calcular: previsualiza una traza sin guardar nada. Al menos 2 puntos. */
/** Respuesta de POST /troncales/:id/rutas y PUT /rutas/:id. */
export interface RutaGuardada extends Ruta {
  /** Avisos no bloqueantes, p. ej. una parada a más de RUTEO_PARADA_DISTANCIA_MAX_M de la traza. */
  avisos: string[];
  /** Viajes PLANIFICADO sin ruta a los que se les asignó esta ruta al quedar como por defecto (D-47). */
  viajesAsignados: number;
}

export interface CalcularRutaBody {
  puntos: Coordenadas[];
}

export interface CalculoRuta {
  traza: LineStringGeoJson;
  km: number;
  duracionMin: number;
}

/** PUT /viajes/:id/ruta: solo con el viaje PLANIFICADO; la ruta tiene que ser activa y de la troncal del viaje. */
export interface CambiarRutaViajeBody {
  rutaId: number;
}

/** Máximo de ids por lista (`rutas` y `paradas`) en GET /ruteo/nombres; si se pasa, 400. */
export const NOMBRES_RUTEO_MAX_IDS = 200;

/** Nombre de una ruta con el de su troncal (para mostrar las reglas de alerta de ámbito RUTA). */
export interface NombreRuta {
  nombre: string;
  troncalId: number;
  troncalNombre: string;
  activa: boolean;
}

/** Nombre de una parada de ruta (`parada_ruta`) con el de su ruta (reglas de alerta de ámbito PARADA_RUTA). */
export interface NombreParada {
  nombre: string;
  rutaId: number;
  rutaNombre: string;
}

/**
 * GET /ruteo/nombres?rutas=1,2&paradas=3,4 (cualquier rol de la torre). Las claves son los ids pedidos; los que no
 * existen se omiten (no hay error). `paradas` son ids de `parada_ruta`, no de `parada_viaje`.
 */
export interface NombresRuteoRespuesta {
  rutas: Record<string, NombreRuta>;
  paradas: Record<string, NombreParada>;
}
